import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Plus, Minus, ShoppingBag, LogIn, UserPlus, Phone, Trash2, CheckCircle2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiError } from "@/lib/api";

const formatCurrency = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));

const cartKey = (slug) => `orden_delivery_cart_${slug}`;

export default function CustomerStorefront() {
  const { slug } = useParams();
  const { user, login } = useAuth();
  const [resto, setResto] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cartKey(slug))) || []; } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null); // 'login' | 'register' | null
  const [authForm, setAuthForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [authBusy, setAuthBusy] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({ customer_name: "", customer_phone: "", delivery_address: "", delivery_references: "", notes: "" });
  const [placing, setPlacing] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const isCustomer = user && user.role === "customer";

  // Hydrate the checkout form when the customer logs in.
  useEffect(() => {
    if (isCustomer) {
      setCheckoutForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_phone: f.customer_phone || user.phone || "",
      }));
    }
  }, [isCustomer, user]);

  useEffect(() => {
    try { localStorage.setItem(cartKey(slug), JSON.stringify(cart)); } catch { /* ignore */ }
  }, [cart, slug]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data: r }, { data: m }] = await Promise.all([
        axios.get(`${API}/r/${slug}`),
        axios.get(`${API}/r/${slug}/menu`),
      ]);
      setResto(r);
      setDishes(m);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Restaurante no disponible.");
    } finally { setLoading(false); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const dishesByCategory = useMemo(() => {
    const groups = {};
    for (const d of dishes) {
      const cat = d.category || "General";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(d);
    }
    return groups;
  }, [dishes]);

  const addToCart = (dish) => {
    setCart((arr) => {
      const idx = arr.findIndex((x) => x.dish_id === dish.id);
      if (idx >= 0) {
        const copy = [...arr];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...arr, { dish_id: dish.id, name: dish.name, price: Number(dish.price || 0), qty: 1 }];
    });
    toast.success(`${dish.name} agregado`);
  };

  const updateQty = (dish_id, delta) => {
    setCart((arr) =>
      arr
        .map((x) => (x.dish_id === dish_id ? { ...x, qty: x.qty + delta } : x))
        .filter((x) => x.qty > 0),
    );
  };

  const removeFromCart = (dish_id) => {
    setCart((arr) => arr.filter((x) => x.dish_id !== dish_id));
  };

  const subtotal = useMemo(() => cart.reduce((s, x) => s + x.price * x.qty, 0), [cart]);
  const cartCount = cart.reduce((s, x) => s + x.qty, 0);

  const submitAuth = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    try {
      if (authMode === "login") {
        await login(authForm.email, authForm.password);
      } else {
        await axios.post(`${API}/customer/register`, authForm, { withCredentials: true });
        // After register, log in to pull /me
        await login(authForm.email, authForm.password);
      }
      setAuthMode(null);
      setCartOpen(true);
      toast.success("¡Bienvenido!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setAuthBusy(false); }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!cart.length) { toast.error("Tu carrito está vacío"); return; }
    if (!checkoutForm.delivery_address.trim()) { toast.error("Falta la dirección de entrega"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post(`/r/${slug}/orders`, {
        ...checkoutForm,
        items: cart.map((c) => ({ dish_id: c.dish_id, quantity: c.qty })),
      });
      setCart([]);
      try { localStorage.removeItem(cartKey(slug)); } catch { /* ignore */ }
      setCartOpen(false);
      setConfirmedOrder(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setPlacing(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#fcfbfd] flex items-center justify-center"><p className="text-[#7248ae]">Cargando…</p></div>;
  }

  if (error || !resto) {
    return (
      <div className="min-h-screen bg-[#fcfbfd] flex items-center justify-center p-6">
        <Card className="p-10 max-w-md text-center border border-[#b182ec]/40">
          <X className="h-12 w-12 mx-auto text-rose-500 mb-3" />
          <h1 className="font-display text-2xl font-bold text-[#1a0f2e] mb-2">No disponible</h1>
          <p className="text-sm text-[#7248ae]">{error || "El restaurante no está aceptando pedidos por ahora."}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fcfbfd] pb-24" data-testid="customer-storefront">
      <header className="bg-[#1a0f2e] text-white">
        <div className="max-w-4xl mx-auto px-5 py-7">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#b182ec] mb-2">Pide a domicilio</p>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tight" data-testid="storefront-name">{resto.name}</h1>
          {resto.address && (
            <p className="mt-2 text-sm text-white/70 flex items-center gap-1"><MapPin className="h-3 w-3" />{resto.address}</p>
          )}
          <div className="mt-4 flex gap-2 flex-wrap">
            {isCustomer ? (
              <span className="px-3 py-1 rounded-full bg-white/10 text-xs text-[#d8baff]">Hola, {user.name}</span>
            ) : (
              <>
                <Button size="sm" onClick={() => setAuthMode("login")} className="bg-white text-[#1a0f2e] hover:bg-[#d8baff]" data-testid="storefront-login-button">
                  <LogIn className="h-3 w-3 mr-1" />Entrar
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAuthMode("register")} className="border-white/40 text-white hover:bg-white/10" data-testid="storefront-register-button">
                  <UserPlus className="h-3 w-3 mr-1" />Crear cuenta
                </Button>
              </>
            )}
            {isCustomer && (
              <Button size="sm" variant="outline" asChild className="border-white/40 text-white hover:bg-white/10">
                <Link to="/customer/orders" data-testid="storefront-my-orders">Mis pedidos</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-6">
        {Object.entries(dishesByCategory).map(([cat, list]) => (
          <section key={cat} className="mb-10" data-testid={`storefront-cat-${cat}`}>
            <h2 className="font-display text-xl font-bold text-[#3e166c] mb-3 uppercase tracking-wide">{cat}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {list.map((d) => (
                <Card key={d.id} className="p-4 border border-[#b182ec]/30 rounded-2xl flex items-start gap-3" data-testid={`storefront-dish-${d.id}`}>
                  {d.photo_url ? (
                    <img src={d.photo_url} alt={d.name} className="h-16 w-16 rounded-xl object-cover" />
                  ) : (
                    <div className="h-16 w-16 rounded-xl bg-[#3e166c]/10 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#1a0f2e] truncate">{d.name}</h3>
                    {d.description && <p className="text-xs text-[#7248ae] line-clamp-2 mt-0.5">{d.description}</p>}
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-bold text-[#3e166c]">{formatCurrency(d.price)}</span>
                      <Button size="sm" onClick={() => addToCart(d)} className="bg-[#3e166c] hover:bg-[#7248ae] text-white">
                        <Plus className="h-3 w-3 mr-1" />Agregar
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        ))}
        {dishes.length === 0 && (
          <p className="text-center text-[#7248ae] py-12">El restaurante todavía no tiene platillos disponibles para delivery.</p>
        )}
      </main>

      {/* Floating cart button */}
      {cartCount > 0 && (
        <button
          data-testid="storefront-cart-button"
          onClick={() => {
            if (!isCustomer) { setAuthMode("login"); return; }
            setCartOpen(true);
          }}
          className="fixed bottom-5 right-5 inline-flex items-center gap-2 bg-[#3e166c] text-white px-5 h-12 rounded-full shadow-2xl hover:bg-[#7248ae] transition-all z-40"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <ShoppingBag className="h-4 w-4" />
          <span className="font-semibold">{cartCount} {cartCount === 1 ? "ítem" : "ítems"}</span>
          <span className="ml-1">{formatCurrency(subtotal)}</span>
        </button>
      )}

      {/* Cart drawer modal */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="storefront-cart-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1a0f2e]">Tu pedido</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {cart.length === 0 ? (
              <p className="text-center text-[#7248ae] py-6">Carrito vacío.</p>
            ) : cart.map((c) => (
              <div key={c.dish_id} className="flex items-center gap-3" data-testid={`cart-item-${c.dish_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#1a0f2e] truncate">{c.name}</p>
                  <p className="text-xs text-[#7248ae]">{formatCurrency(c.price)} c/u</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-[#b182ec]/50" onClick={() => updateQty(c.dish_id, -1)}><Minus className="h-3 w-3" /></Button>
                <span className="w-6 text-center font-bold">{c.qty}</span>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-[#b182ec]/50" onClick={() => updateQty(c.dish_id, +1)}><Plus className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 h-7 w-7 p-0" onClick={() => removeFromCart(c.dish_id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
            {cart.length > 0 && (
              <div className="border-t border-[#b182ec]/30 pt-3 flex justify-between font-bold text-[#1a0f2e]">
                <span>Subtotal</span><span>{formatCurrency(subtotal)}</span>
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <form onSubmit={placeOrder} className="space-y-3 pt-3 border-t border-[#b182ec]/30" data-testid="checkout-form">
              <h3 className="font-display text-lg font-bold text-[#1a0f2e]">Datos de entrega</h3>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Nombre</Label>
                <Input value={checkoutForm.customer_name} onChange={(e) => setCheckoutForm({ ...checkoutForm, customer_name: e.target.value })} required data-testid="checkout-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Teléfono</Label>
                <Input value={checkoutForm.customer_phone} onChange={(e) => setCheckoutForm({ ...checkoutForm, customer_phone: e.target.value })} required data-testid="checkout-phone" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Dirección de entrega</Label>
                <Textarea rows={2} value={checkoutForm.delivery_address} onChange={(e) => setCheckoutForm({ ...checkoutForm, delivery_address: e.target.value })} required placeholder="Calle, número, colonia" data-testid="checkout-address" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Referencias</Label>
                <Textarea rows={2} value={checkoutForm.delivery_references} onChange={(e) => setCheckoutForm({ ...checkoutForm, delivery_references: e.target.value })} placeholder="Edificio azul, frente al parque, depto 4…" data-testid="checkout-references" />
              </div>
              <Button type="submit" disabled={placing} className="w-full bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="checkout-place-order">
                <CheckCircle2 className="h-4 w-4 mr-2" />{placing ? "Enviando…" : "Realizar pedido"}
              </Button>
              <p className="text-[10px] text-center text-[#7248ae]">El restaurante te enviará por correo el link de pago una vez confirme tu pedido.</p>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Auth modal */}
      <Dialog open={!!authMode} onOpenChange={(v) => !v && setAuthMode(null)}>
        <DialogContent className="bg-white" data-testid="storefront-auth-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1a0f2e]">
              {authMode === "login" ? "Entrar a tu cuenta" : "Crear cuenta"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAuth} className="space-y-3 py-2">
            {authMode === "register" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Nombre</Label>
                  <Input value={authForm.name} onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })} required data-testid="auth-name-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Teléfono</Label>
                  <Input value={authForm.phone} onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })} required data-testid="auth-phone-input" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Correo</Label>
              <Input type="email" autoComplete="email" value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} required data-testid="auth-email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Contraseña</Label>
              <Input type="password" autoComplete={authMode === "login" ? "current-password" : "new-password"} value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} required data-testid="auth-password-input" />
            </div>
            <Button type="submit" disabled={authBusy} className="w-full bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="auth-submit-button">
              {authBusy ? "Procesando…" : (authMode === "login" ? "Entrar" : "Crear cuenta")}
            </Button>
            <button type="button" onClick={() => setAuthMode(authMode === "login" ? "register" : "login")} className="block mx-auto text-xs text-[#7248ae] hover:text-[#3e166c]">
              {authMode === "login" ? "¿No tienes cuenta? Crear una" : "¿Ya tienes cuenta? Entrar"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation modal */}
      <Dialog open={!!confirmedOrder} onOpenChange={(v) => !v && setConfirmedOrder(null)}>
        <DialogContent className="bg-white" data-testid="storefront-order-confirmation">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1a0f2e]">¡Pedido recibido!</DialogTitle>
          </DialogHeader>
          {confirmedOrder && (
            <div className="text-center py-4">
              <div className="inline-flex h-16 w-16 rounded-full bg-emerald-100 items-center justify-center mb-3">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <p className="font-display text-2xl font-bold text-[#1a0f2e] mb-1">{confirmedOrder.order_number}</p>
              <p className="text-sm text-[#7248ae] mb-4">
                Total estimado: <strong className="text-[#3e166c]">{formatCurrency(confirmedOrder.total)}</strong>
              </p>
              <p className="text-xs text-[#7248ae] mb-4">
                {resto.name} revisará tu pedido y te enviará por correo el link de pago. Te avisaremos cuando salga a entrega.
              </p>
              <div className="flex gap-2 justify-center">
                <Button asChild className="bg-[#3e166c] hover:bg-[#7248ae] text-white"><Link to="/customer/orders">Ver mis pedidos</Link></Button>
                <Button variant="outline" onClick={() => setConfirmedOrder(null)} className="border-[#b182ec]/50">Seguir viendo</Button>
              </div>
              {resto.phone && (
                <p className="text-xs text-[#7248ae] mt-3 flex items-center justify-center gap-1">
                  <Phone className="h-3 w-3" />Si necesitas algo: {resto.phone}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
